/** Talking to the API (same site, under /api) */
import { CHAT_HOST, DRAFT_ORIGIN, HOME_HOST } from './sites';
import type { DraftLeague } from '@ogreclan/league-core';

export interface SessionUser {
	id: string;
	name: string;
	avatar: string | null;
	discord: { id: string, name: string, username: string | null } | null;
	showdown: { id: string, name: string } | null;
	isAdmin: boolean;
	/** Staff in every league: admins, and moderators on OgreClan's Showdown server */
	isModerator: boolean;
	/** Global Admin: can link a Showdown name to someone else's account */
	isGlobalAdmin: boolean;
	/** When an admin is viewing the site as this account (read-only): who they really are */
	viewedBy: { id: string, name: string } | null;
}

export interface Me {
	user: SessionUser | null;
	discordSignIn: boolean;
}

export type Role = 'staff' | 'player' | 'viewer';

export interface LeagueSummary {
	roomid: string;
	title: string;
	isPrivate: boolean;
	role: Role;
	phase: DraftLeague['phase'];
	week: number;
	format: string;
	type: 'points' | 'free';
	players: number;
	updatedAt: string;
	you: string | null;
	/** It's this Discord league's copy for the Draft Planner: its address shows that league */
	botLeague: number | null;
}

export interface LeagueView {
	roomid: string;
	title: string;
	isPrivate: boolean;
	mod: string;
	revision: number;
	updatedAt: string;
	role: Role;
	/** Your player id in this league, if you're playing */
	you: string | null;
	league: DraftLeague;
	/** It's this Discord league's copy for the Draft Planner: its address shows that league */
	botLeague: number | null;
}

export interface LeagueEvent {
	id: number;
	revision: number;
	actor: string;
	source: 'showdown' | 'web' | 'server';
	action: string;
	detail: string;
	createdAt: string;
}

export interface ActionResult {
	ok: boolean;
	replies: string[];
	error?: string;
}

export class ApiError extends Error {
	status: number;
	/** The whole answer, for errors that say more (like a team's problems) */
	data: Record<string, unknown>;
	constructor(message: string, status: number, data: Record<string, unknown> = {}) {
		super(message);
		this.status = status;
		this.data = data;
	}
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
	let response: Response;
	try {
		response = await fetch(path, {
			method,
			headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
			body: body === undefined ? undefined : JSON.stringify(body),
			credentials: 'same-origin',
		});
	} catch {
		throw new ApiError(`Couldn't reach the site. Check your connection and try again.`, 0);
	}
	const data = await response.json().catch(() => ({})) as { error?: string };
	if (!response.ok) throw new ApiError(data.error || `Something went wrong (${response.status}).`, response.status, data);
	return data as T;
}

export const api = {
	get: <T>(path: string) => request<T>('GET', path),
	post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
	put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
	patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
	delete: <T>(path: string) => request<T>('DELETE', path),
};

/** Where "Sign in" goes, coming back to this page afterwards */
export function signInUrl(next = location.pathname + location.search) {
	// chat.ogreclan.com and ogreclan.com sign in on draft.ogreclan.com (where Discord sends people back to), and come back
	// here (the API allows its own other addresses, EXTRA_ORIGINS); the sign-in cookie is for every ogreclan.com address
	if (CHAT_HOST || HOME_HOST) {
		return `${DRAFT_ORIGIN}/api/auth/discord?next=${encodeURIComponent(`${location.origin}${next}`)}`;
	}
	return `/api/auth/discord?next=${encodeURIComponent(next)}`;
}

/* The OCDL Discord bot's leagues (read from its database; see apps/api/src/bot-leagues.ts) */

export interface BotLeagueSummary {
	id: number;
	name: string;
	mode: string;
	status: string;
	budget: number;
	teraBudget: number;
	createdAt: string;
	startedAt: string | null;
	finishedAt: string | null;
	pools: { label: string, status: string, players: number }[];
	/** Only its own players (and site admins) can see it */
	private: boolean;
	/** Its Showdown room, whose address (/league/{room}) is this league's */
	room: string | null;
	you: boolean;
}

export interface BotPlayer {
	id: number;
	name: string;
	seat: number;
	coachNumber: number;
	budgetRemaining: number;
	teraBudgetRemaining: number;
	timer: number;
	skips: number;
	status: string;
	roster: { pokemon: string, cost: number, teraCaptain: boolean, source: string }[];
	owedRounds: number[];
}

export interface BotPool {
	label: string;
	status: string;
	round: number;
	onTheClock: number | null;
	turnDeadline: number | null;
	scheduledStart: number | null;
	startedAt: string | null;
	finishedAt: string | null;
	/** Unix seconds: until then (after the draft), trades don't count toward the limit */
	freeTradesUntil: number | null;
	you: number | null;
	players: BotPlayer[];
	picks: { round: number, playerId: number, pokemon: string, cost: number, teraCaptain: boolean, order: number, makeup: boolean, auto: boolean, at: string }[];
	skips: { playerId: number, round: number, reason: string, at: string }[];
}

export interface BotLeague {
	id: number;
	name: string;
	/** You made this league as an approved host, so you run it */
	youHost?: boolean;
	mode: string;
	status: string;
	budget: number;
	teraBudget: number;
	minPicks: number;
	maxPicks: number;
	timer: number;
	maxTrades: number;
	seasonWeeks: number;
	private: boolean;
	/** Uses the site for picks, queues and Ready matches */
	website: boolean;
	/** Made on the site and lives only here (no Discord) */
	siteOnly: boolean;
	docKind: string;
	docUrl: string | null;
	createdAt: string;
	startedAt: string | null;
	finishedAt: string | null;
	pools: BotPool[];
	/** The week last posted with /postschedule (0 before the first) */
	currentWeek: number;
	/** Its Showdown room, whose address (/league/{room}) is this league's */
	room: string | null;
	/** Approved trades: in effect from a week, or waiting for the next one to be posted */
	trades: {
		number: number, proposer: number, partner: number | null, drops: string[], gets: string[], at: string | null,
		status: 'pending' | 'approved' | 'rejected', inEffect: boolean, week: number | null, free: boolean,
	}[];
	fixtures: { week: number, match: number, home: number, away: number }[];
	/** Everyone in the league, from matches that are over, best first */
	standings: {
		player: number, name: string, played: number, wins: number, losses: number, gameWins: number, gameLosses: number,
		/** forfeitKd: +6 for each forfeit won, -6 for each one lost; diff: kills minus deaths, with forfeits */
		kills: number, deaths: number, forfeitKd: number, diff: number,
	}[];
	/** Each player's Pokémon by kills and deaths over the season, best first */
	topPokemon: { player: number, pokemon: string, games: number, kills: number, deaths: number }[];
}

/** What's been reported for a match (the bot keeps it): games, kills and deaths, Champions, Pokepastes */
export interface BotMatchResult {
	week: number;
	match: number;
	champions: boolean;
	asked: number[];
	winner: number | null;
	/** Awarded by staff, not played out: a forfeit or a no-show */
	walkover: boolean;
	games: {
		game: number, winner: number | null, source: string, replay: string | null,
		stats: { player: number, pokemon: string, kills: number, deaths: number }[],
	}[];
	pokepastes: Record<string, string>;
}

/** A bot league pool's whole tiering list */
export interface BotTierList {
	available: boolean;
	entries: { name: string, cost: number | null, tera_cost: number | null, banned: boolean, tera_banned: boolean, taken_by: number | null }[];
}

/** What's still draftable in a bot league, with its costs, from the league's spreadsheet */
export interface BotPrices {
	available: boolean;
	entries: { name: string, cost: number, tera_cost: number | null }[];
}

/** Your own pick queues in a bot league (nobody else can see them) */
export interface BotQueue {
	rounds: { round: number, entries: { priority: number, pokemon: string, cost: number | null, tera_captain: boolean }[] }[];
	/** Rounds you've already drafted in */
	picked: number[];
}
